import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { Dialog, DialogContent } from "@mui/material";
import { ColorRing } from "react-loader-spinner";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useNavigate } from "react-router-dom";
import ContactAccordion from "../../accordion/BuildingAccordion";
import RenderFields from "../Renders/RenderFields";
import RenderFieldsDropdown from "../Renders/RenderFieldsDropdown";
import BuildingContactTable from "../Tables/BuildingContactTable";
import NewBuildingContactForm from "../Forms/NewBuildingContactForm";
import BuildingDeviceTable from "../Tables/BuildingDeviceTable";
import InspectionWizardController from "../../../../../setup/components/Building/Actions/Run the Inspection Wizard/InspectionWizardModalNoDropdown";

import NewBuildingDeviceForm from "../Forms/NewBuildingDeviceForm";
import RenderFieldsVirtualizedAutocomplete from "../../../../../../components/Dropdown/VirtualizedAutocompleteStyled";
import {
  BuildingFields,
  SalesMaintenanceFields,
  BuildingTypes,
  AddressFields,
  BillingAddressFields,
  DispatchTypes,
  StatusOptions,
  BuildingStatusFields,
} from "../../config/formDataConfig";
import useTabManager from "../../hooks/useTabManager";
import TabPanel from "../sidebar/TabPanel";
import API_BASE_URL from "../../../../../../Constants";
import TabPanelButtonGroup from "../sidebar/TabPanelButtonGroup";

import { useSelector } from "react-redux";
import { selectNavHeight } from "../../../../../../redux/navbar/navHeightSlice";

const ViewBuildingModal = ({
  open,
  selectedBuildingData,
  refreshBuildings,
  onClose,
  setNext,
  setPrev,
}) => {
  const navigate = useNavigate();

  const navHeight = useSelector(selectNavHeight);

  const [buildingData, setBuildingData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [contactData, setContactData] = useState([]);
  const [deviceData, setDeviceData] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [salesReps, setSalesReps] = useState([]);
  const [allContacts, setAllContacts] = useState([]);
  const [allDevices, setAllDevices] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const [isInspectionsWizardOpen, setIsInspectionsWizardOpen] = useState(false);
  const [newDeviceId, setNewDeviceId] = useState("");
  const [showNewDeviceForm, setShowNewDeviceForm] = useState(false);
  const [deviceForm, setDeviceForm] = useState({
    deviceId: "",
    deviceType: "",
    deviceStatus: "",
    deviceDomain: "",
    block: "",
    bin: "",
  });
  const [assignedDevices, setAssignedDevices] = useState([]);
  const [newContactId, setNewContactId] = useState("");
  const [showNewContactForm, setShowNewContactForm] = useState(false);
  const [contactForm, setContactForm] = useState({
    contactName: "",
    mainPhone: "",
    cellPhone: "",
    emailAddress: "",
  });
  const [assignedContacts, setAssignedContacts] = useState([]);
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);
  const [activeAddressSection, setActiveAddressSection] = useState("address");
  const [customer, setCustomer] = useState(null);
  const [agency, setAgency] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [assignedInspections, setAssignedInspections] = useState([]);
  const [selectedInspection, setSelectedInspection] = useState(null);
  const [violations, setViolations] = useState([]);
  const [selectedViolation, setSelectedViolation] = useState(null);
  const [selectedTabs, setSelectedTabs] = useState([]);
  const isDrawerOpen = selectedTabs.length > 0;
  const dialogPaperRef = useRef(null);
  const { leftTabs, rightTabs, editFieldHandler } = useTabManager(selectedTabs);
  const totalWidth =
    selectedTabs.length > 2 ? "2200px" : isDrawerOpen ? "1600px" : "1000px";
  const panelWidth = leftTabs.length > 0 ? "600px" : "0px";
  const rightPanelWidth = rightTabs.length > 0 ? "600px" : "0px";

  const fetchCustomerContacts = async (customerId) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/setup/customer-contact-intersections/customer/${customerId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
        }
      );
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching customers:", error);
      return [];
    }
  };

  useEffect(() => {
    const handleCustomerContacts = async () => {
      if (
        isEditMode &&
        buildingData.customerID &&
        window.confirm("Do you want to import customer contacts/address?")
      ) {
        try {
          const contacts = await fetchCustomerContacts(buildingData.customerID);
          const contactsRoles = contacts.map((assignedContact) => {
            const contactDetails = allContacts.find(
              (contact) => contact.contactId === assignedContact.contactId
            );
            if (!contactDetails) {
              console.warn(
                `Contact details not found for contactId: ${assignedContact.contactId}`
              );
              return {
                ...assignedContact,
                isEditing: false,
                buildingId: buildingData.buildingID,
                superintendentContact: false,
                dobContact: false,
              };
            }
            return {
              ...assignedContact,
              ...contactDetails,
              isEditing: false,
              buildingId: buildingData.buildingID,
              superintendentContact: false,
              dobContact: false,
            };
          });
          setAssignedContacts(contactsRoles);
          await saveAssignedContacts(contactsRoles);

          if (selectedCustomer) {
            setBuildingData((prevData) => ({
              ...prevData,
              addressCareOf: selectedCustomer.addressCareOf,
              addressLine1: selectedCustomer.address,
              addressLine2: selectedCustomer.addressTwo,
              city: selectedCustomer.city,
              state: selectedCustomer.state,
              zip: selectedCustomer.zip,
              billingAddressCareOf: selectedCustomer.billingAddressCareOf,
              billingAddressLine1: selectedCustomer.billingAddress,
              billingAddressLine2: selectedCustomer.billingAddressTwo,
              billingCity: selectedCustomer.billingCity,
              billingState: selectedCustomer.billingState,
              billingZip: selectedCustomer.billingZip,
            }));
          }
        } catch (error) {
          console.error("Error fetching customer contacts:", error);
        }
      }
    };

    handleCustomerContacts();
  }, [buildingData.customerID]);

  const handleClose = (event, reason) => {
    if (isEditMode) {
      setShowCloseConfirmation(true);
    } else {
      onClose(event, reason);
    }
  };

  const renderError = (fieldKey) => {
    return errors[fieldKey] ? (
      <p className="text-red-500 text-sm mt-1">{errors[fieldKey]}</p>
    ) : null;
  };

  const fetchBuildingData = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = selectedBuildingData;
      data.salesRep = data.salesRep ? data.salesRep.toString().trim() : "";
      data.buildingType = data.buildingType ? data.buildingType.toString() : "";
      data.buildingStatus = data.buildingStatus
        ? data.buildingStatus.toString()
        : "";
      data.dispatchType = data.dispatchType ? data.dispatchType.toString() : "";
      if (data.buildingBalance) {
        data.buildingBalance = parseFloat(data.buildingBalance);
      }
      setBuildingData(data);
      const fetchPromises = [];

      if (data.customerID) {
        fetchPromises.push(fetchCustomerData(data.customerID));
      }
      fetchPromises.push(fetchCustomers());
      fetchPromises.push(fetchSalesReps());
      fetchPromises.push(fetchAllContacts());
      fetchPromises.push(fetchAllDevices());
      if (data.addressID) {
        fetchPromises.push(fetchAddressData(data.addressID));
      }
      fetchPromises.push(fetchContacts(data.buildingID));
      fetchPromises.push(fetchDevices(data.buildingID));
      fetchPromises.push(fetchAgency(data.agencyID));
      await Promise.all(fetchPromises);
    } catch (error) {
      console.error("Error fetching building:", error);
    } finally {
      setIsLoading(false);
    }
  }, [selectedBuildingData]);

  useEffect(() => {
    fetchBuildingData();
  }, [fetchBuildingData]);

  const fetchCustomerData = async (customerID) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/setup/customers/${customerID}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch customer data");
      }
      const customerData = (await response.json()) || {};
      setCustomer(customerData);
      setBuildingData((prevData) => ({
        ...prevData,
        managementID: customerData.managementID?.toString() || "",
      }));
    } catch (error) {
      console.error("Error fetching customer data:", error);
    }
  };

  const buildingFieldsWithOptions = useMemo(() => {
    return BuildingFields.map((field) => {
      if (field.key === "customerID") {
        return {
          ...field,
          options: customers.map((customer) => ({
            key: customer.customerID.toString(),
            value: customer.customerName || "No Name",
          })),
        };
      }
      return field;
    });
  }, [BuildingFields, customers]);

  const fetchAddressData = async (addressID) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/setup/buildings/addresses/${addressID}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch address data");
      }
    } catch (error) {
      console.error("Error fetching address:", error);
    }
  };

  const fetchAllContacts = async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/setup/contacts/get-all`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
        }
      );
      if (!response.ok) {
        throw new Error("Failed to fetch all contacts");
      }
      const data = await response.json();
      setAllContacts(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching all contacts:", error);
    }
  };

  const fetchContacts = async (buildingID) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/setup/building-contact-intersections/building/${buildingID}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
        }
      );
      if (!response.ok) {
        throw new Error("Failed to fetch contacts");
      }
      const data = await response.json();
      setContactData(data || []);
    } catch (error) {
      console.error("Error fetching contacts:", error);
    }
  };

  const fetchDevices = async (buildingId) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/setup/buildings/devices/${buildingId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
        }
      );
      if (!response.ok) {
        throw new Error("Failed to fetch devices");
      }
      const data = await response.json();
      setDeviceData(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching devices:", error);
      setDeviceData([]);
    }
  };

  const fetchAllDevices = async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/setup/buildings/devices/all`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
        }
      );
      if (!response.ok) {
        throw new Error("Failed to fetch all devices");
      }
      const data = await response.json();
      setAllDevices(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching all devices:", error);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/setup/customers/all`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error("Failed to fetch customers");
      }
      const data = await response.json();
      setCustomers(data);
    } catch (error) {
      console.error("Error fetching customers:", error);
    }
  };

  const fetchSalesReps = async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/setup/salesperson/salespersons`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
        }
      );
      if (!response.ok) {
        throw new Error("Failed to fetch sales representatives");
      }
      const data = await response.json();
      const salesRepsData = data.map((rep) => ({
        ...rep,
        SalesID:
          rep.SalesID !== undefined && rep.SalesID !== null
            ? rep.SalesID.toString()
            : "",
      }));
      setSalesReps(salesRepsData);
    } catch (error) {
      console.error("Error fetching sales representatives:", error);
    }
  };

  const fetchAgency = async (agencyId) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/inspections/agency/agencies/${agencyId}`,
        { credentials: "include" }
      );
      if (!response.ok) {
        throw new Error("Failed to fetch agency");
      }
      const data = await response.json();
      setAgency(data);
    } catch (error) {
      console.error("Error fetching agency:", error);
    }
  };

  const fetchViolations = async (inspectionId) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/violations/byInspection/${inspectionId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
        }
      );
      if (!response.ok) {
        throw new Error("Failed to fetch violations");
      }
      const data = await response.json();
      setViolations(data || []);
    } catch (error) {
      console.error("Error fetching violations:", error);
    }
  };

  const fetchAssignedInspections = async (buildingID) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/inspections/building/${buildingID}`,
        {
          method: "GET",
          credentials: "include",
        }
      );
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      setAssignedInspections(data || []);
    } catch (err) {
      console.error("Error fetching assigned inspections:", err);
    }
  };

  useEffect(() => {
    if (!buildingData.buildingID) return;
    fetchAssignedInspections(buildingData.buildingID);
  }, [buildingData.buildingID]);

  useEffect(() => {
    const fetchManagementCompany = async () => {
      if (buildingData.managementID) {
        try {
          const response = await fetch(
            `${API_BASE_URL}/api/setup/managementcompanies/${buildingData.managementID}`,
            {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
              },
              credentials: "include",
            }
          );
          if (!response.ok) {
            throw new Error("Failed to fetch management company");
          }
          const managementCompanyData = await response.json();
          setBuildingData((prevData) => ({
            ...prevData,
            companyName: managementCompanyData.companyName || "N/A",
          }));
        } catch (error) {
          console.error("Error fetching management company:", error);
          setBuildingData((prevData) => ({
            ...prevData,
            companyName: "N/A",
          }));
        }
      } else {
        setBuildingData((prevData) => ({
          ...prevData,
          companyName: "N/A",
        }));
      }
    };
    fetchManagementCompany();
  }, [buildingData.managementID]);

  useEffect(() => {
    if (customer) {
      setBuildingData((prevData) => ({
        ...prevData,
        addressCareOf: customer.addressCareOf || "",
        addressLine1: customer.address || "",
        addressLine2: customer.addressTwo || "",
        city: customer.city || "",
        state: customer.state || "",
        zip: customer.zip || "",
        billingAddressCareOf: customer.billingAddressCareOf || "",
        billingAddressLine1: customer.billingAddress || "",
        billingAddressLine2: customer.billingAddressTwo || "",
        billingCity: customer.billingCity || "",
        billingState: customer.billingState || "",
        billingZip: customer.billingZip || "",
      }));
    }
  }, [customer]);

  useEffect(() => {
    setAssignedContacts(
      contactData.map((contact) => ({ ...contact, isEditing: false }))
    );
  }, [contactData]);

  useEffect(() => {
    setAssignedDevices(
      deviceData.map((device) => ({ ...device, isEditing: false }))
    );
  }, [deviceData]);

  const toggleEditMode = () => {
    if (isEditMode) {
      handleSubmit();
    } else {
      setIsEditMode(true);
    }
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    if (name === "state" && value.length > 2) return;
    if (name === "zip" && (value.length > 5 || !/^\d*$/.test(value))) return;
    if (name === "billingState" && value.length > 2) return;
    if (name === "billingZip" && (value.length > 5 || !/^\d*$/.test(value)))
      return;
    setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));

    let updatedBuildingData = {
      ...buildingData,
      [name]: value,
    };

    if (name === "customerID") {
      const selectedCust = customers.find(
        (c) => c.customerID.toString() === value.toString()
      );
      setSelectedCustomer(selectedCust);
      updatedBuildingData.managementID =
        selectedCust?.managementID?.toString() || "";
    }
    setBuildingData(updatedBuildingData);
  };

  const handleContactFormChange = (e) => {
    const { name, value } = e.target;
    setContactForm((prevForm) => ({
      ...prevForm,
      [name]: value,
    }));
  };

  const toggleNewContactForm = () => {
    setShowNewContactForm(!showNewContactForm);
  };

  const handleSaveNewContact = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/setup/contacts`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(contactForm),
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error("Failed to create contact");
      }
      const responseData = await response.json();
      const contactId = responseData.contactId || responseData.contactID;
      if (!contactId) {
        throw new Error("Failed to retrieve contactId from the response.");
      }
      const existingContactsResponse = await fetch(
        `${API_BASE_URL}/api/setup/building-contact-intersections/building/${buildingData.buildingID}`,
        {
          method: "GET",
          credentials: "include",
        }
      );
      if (!existingContactsResponse.ok) {
        throw new Error("Failed to fetch existing contacts");
      }
      const existingContacts = await existingContactsResponse.json();
      const updatedContacts = [
        ...existingContacts,
        {
          buildingId: buildingData.buildingID,
          contactId: contactId,
          mainContact: false,
          superintendentContact: false,
        },
      ];
      const putResponse = await fetch(
        `${API_BASE_URL}/api/setup/building-contact-intersections/update/${buildingData.buildingID}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(updatedContacts),
          credentials: "include",
        }
      );
      if (!putResponse.ok) {
        throw new Error("Failed to update contacts");
      }
      const putResponseData = await putResponse.json();
      console.log("Response from updating contacts:", putResponseData);

      await fetchContacts(buildingData);
      setShowNewContactForm(false);
      setContactForm({
        contactName: "",
        mainPhone: "",
        cellPhone: "",
        emailAddress: "",
      });
    } catch (error) {
      console.error("Error saving new contact:", error);
      if (error.response && error.response.data) {
        console.error("Server response:", error.response.data);
      }
      alert(`An error occurred while saving the new contact: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelNewContact = () => {
    setShowNewContactForm(false);
    setContactForm({
      contactName: "",
      mainPhone: "",
      cellPhone: "",
      emailAddress: "",
    });
  };

  const addContact = async () => {
    if (newContactId) {
      try {
        const existingContactsResponse = await fetch(
          `${API_BASE_URL}/api/setup/building-contact-intersections/building/${buildingData}`,
          {
            method: "GET",
            credentials: "include",
          }
        );
        if (!existingContactsResponse.ok) {
          throw new Error("Failed to fetch existing contacts");
        }
        const existingContacts = (await existingContactsResponse.json()) || [];
        const alreadyAssigned = existingContacts.some(
          (contact) =>
            (contact.contactId || contact.contactID)?.toString() ===
            newContactId.toString()
        );
        if (alreadyAssigned) {
          alert("Contact is already assigned");
          return;
        }
        const updatedContacts = [
          ...existingContacts,
          {
            buildingId: buildingData.buildingID,
            contactId: newContactId,
            mainContact: false,
            superintendentContact: false,
          },
        ];
        const putResponse = await fetch(
          `${API_BASE_URL}/api/setup/building-contact-intersections/update/${buildingData.buildingID}`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(updatedContacts),
            credentials: "include",
          }
        );
        if (!putResponse.ok) {
          throw new Error("Failed to update contacts");
        }
        const putResponseData = await putResponse.json();
        console.log("Response from updating contacts:", putResponseData);
        await fetchContacts(buildingData.buildingID);
        setNewContactId("");
      } catch (error) {
        console.error("Error adding contact:", error);
        if (error.response && error.response.data) {
          console.error("Server response:", error.response.data);
        }
        alert("An error occurred while adding the contact");
      }
    } else {
      alert("Please select a contact to add");
    }
  };

  const removeContact = useCallback(
    async (index) => {
      const contactToRemove = assignedContacts[index];
      console.log("Contact to remove:", contactToRemove);
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/setup/building-contact-intersections/${buildingData.buildingID}/${contactToRemove.contactId}`,
          {
            method: "DELETE",
            credentials: "include",
          }
        );
        if (!response.ok) {
          throw new Error("Failed to remove contact");
        }
        await fetchContacts(buildingData.buildingID);
      } catch (error) {
        console.error("Error removing contact:", error);
      }
    },
    [assignedContacts, buildingData.buildingID]
  );

  const updateContact = async (contactID, updatedContact) => {
    try {
      console.log("Updating contact:", contactID, updatedContact);
      const response = await fetch(
        `${API_BASE_URL}/api/setup/contacts/${contactID}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(updatedContact),
          credentials: "include",
        }
      );
      if (!response.ok) {
        throw new Error("Failed to update contact");
      }
      alert("Contact updated successfully");
    } catch (error) {
      console.error("Error updating contact:", error);
      alert("An error occurred while updating the contact");
    }
  };

  const handleDeviceFormChange = (e) => {
    const { name, value } = e.target;
    setDeviceForm((prevForm) => ({
      ...prevForm,
      [name]: value,
    }));
  };

  const toggleNewDeviceForm = () => {
    setShowNewDeviceForm(!showNewDeviceForm);
  };

  const handleSaveNewDevice = async () => {
    setIsLoading(true);
    try {
      const devicePayload = {
        ...deviceForm,
        buildingId: buildingData.buildingID,
      };
      const response = await fetch(
        `${API_BASE_URL}/api/setup/buildings/devices/assign`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(devicePayload),
          credentials: "include",
        }
      );
      if (!response.ok) {
        throw new Error("Failed to create and assign device");
      }
      const responseData = await response.json();
      console.log("Response from creating and assigning device:", responseData);
      await fetchDevices(buildingData.buildingID);
      setShowNewDeviceForm(false);
      setDeviceForm({
        deviceId: "",
        deviceType: "",
        deviceStatus: "",
        deviceDomain: "",
        block: "",
        bin: "",
      });
    } catch (error) {
      console.error("Error saving new device:", error);
      alert(`An error occurred while saving the new device: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelNewDevice = () => {
    setShowNewDeviceForm(false);
    setDeviceForm({
      deviceId: "",
      deviceType: "",
      deviceStatus: "",
      deviceDomain: "",
      block: "",
      bin: "",
    });
  };

  const addDevice = async () => {
    if (newDeviceId) {
      try {
        const alreadyAssigned = assignedDevices.some(
          (device) => device.deviceId.toString() === newDeviceId.toString()
        );
        if (alreadyAssigned) {
          alert("Device is already assigned to this building");
          return;
        }
        const response = await fetch(
          `${API_BASE_URL}/api/setup/buildings/devices/${newDeviceId}`,
          {
            method: "GET",
            credentials: "include",
          }
        );
        if (!response.ok) {
          throw new Error("Failed to fetch device data");
        }
        const deviceDataFetched = await response.json();
        const updatedDeviceData = {
          ...deviceDataFetched,
          buildingId: buildingData.buildingID,
        };
        const putResponse = await fetch(
          `${API_BASE_URL}/api/setup/buildings/devices/${newDeviceId}`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(updatedDeviceData),
            credentials: "include",
          }
        );
        if (!putResponse.ok) {
          throw new Error("Failed to update the device");
        }
        await fetchDevices(buildingData.buildingID);
        setNewDeviceId("");
      } catch (error) {
        console.error("Error adding device:", error);
        alert("An error occurred while adding the device");
      }
    } else {
      alert("Please select a device to add");
    }
  };

  const removeDevice = async (index) => {
    const deviceToRemove = assignedDevices[index];
    try {
      const updatedDevice = {
        ...deviceToRemove,
        buildingId: null,
      };
      const response = await fetch(
        `${API_BASE_URL}/api/setup/buildings/devices/${deviceToRemove.deviceId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(updatedDevice),
          credentials: "include",
        }
      );
      if (!response.ok) {
        throw new Error("Failed to remove device");
      }
      await fetchDevices(buildingData.buildingID);
    } catch (error) {
      console.error("Error removing device:", error);
      alert("An error occurred while removing the device");
    }
  };

  const updateDevice = async (deviceId, updatedDev) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/setup/buildings/devices/${deviceId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(updatedDev),
          credentials: "include",
        }
      );
      if (!response.ok) {
        throw new Error("Failed to update device");
      }
      alert("Device updated successfully");
    } catch (error) {
      console.error("Error updating device:", error);
      alert("An error occurred while updating the device");
    }
  };

  const saveAssignedContacts = async (contactRoles) => {
    console.log("Assigned Contacts:", assignedContacts);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/setup/building-contact-intersections/update/${buildingData.buildingID}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify(contactRoles),
        }
      );
      const data = await response.json();
      console.log("Created Assigned Contacts:", data);
    } catch (error) {
      console.error("Error creating contacts:", error);
    }
  };

  const validateFields = () => {
    const requiredFields = ["dispatchType", "buildingType", "buildingStatus"];
    const newErrors = {};
    requiredFields.forEach((field) => {
      if (!buildingData[field]) {
        newErrors[field] = "This field is required";
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateFields()) {
      alert("Please fill out all required fields.");
      return;
    }
    try {
      setIsEditMode(false);
      setIsLoading(true);
      const response = await fetch(
        `${API_BASE_URL}/api/setup/buildings/${buildingData.buildingID}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(buildingData),
          credentials: "include",
        }
      );
      if (!response.ok) {
        throw new Error("Failed to save building data");
      }
      alert("Building data saved successfully");
      if (refreshBuildings) refreshBuildings();
      await fetchBuildingData();
    } catch (error) {
      console.error("Error saving building data:", error);
      alert("An error occurred while saving building data");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this building? This action cannot be undone."
    );
    if (confirmDelete) {
      setIsLoading(true);
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/setup/buildings/${buildingData.buildingID}`,
          {
            method: "DELETE",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include",
          }
        );
        if (response.ok && response.status === 204) {
          alert("Building deleted successfully");
          if (refreshBuildings) refreshBuildings();
          onClose();
        } else if (response.status === 400) {
          const errorMessage = await response.text();
          alert(
            errorMessage ||
              "The building has associated devices and cannot be deleted."
          );
        } else if (response.status === 403) {
          alert("You do not have permission to delete this building.");
        } else {
          alert("An unexpected error occurred. Please try again later.");
        }
      } catch (error) {
        alert(`Error: ${error.message}`);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleAddDevice = () => {
    navigate(
      `/setup/add-device?buildingId=${buildingData.buildingID}&buildingSite=${buildingData.addressLine1}&address=${buildingData.city}`
    );
  };

  const handleAccountCollections = () => {
    navigate(`/setup/account-collections/${buildingData.buildingID}`);
  };

  // *** The only place we changed: now we pass building ID & agency ID in the route. ***
  const NavigateInspectionWizard = () => {
    navigate(
      `/inspections-violations/inspections-wizard?buildingId=${buildingData.buildingID}&agencyId=${buildingData.agencyID}`
    );
  };

  const NavigateBuildingLedger = () => {
    navigate(`/setup/view-building-ledger/${buildingData.buildingID}`);
  };

  const NavigateMaintenanceContractWizard = () => {
    navigate(`/maintenance/maintenance-contract-entry`, {
      state: {
        buildingId: buildingData.buildingID,
        buildingSite: buildingData.addressLine1,
      },
    });
  };

  const NavigateProposalWizard = () => {
    navigate(`/setup/proposal-wizard`);
  };

  const NavigateBuildingDeviceEditing = () => {
    navigate(`/setup/device-type-editing/${buildingData.buildingID}`);
  };

  const NavigateDOBPostings = () => {
    navigate(`/setup/dob-postings/${buildingData.buildingID}`);
  };

  const NavigateRecordViewer = () => {
    navigate("/setup/buildingrecords", {
      state: { user: "", buildingId: buildingData.buildingID },
    });
  };

  const salesFieldsWithOptions = [
    {
      key: "salesRep",
      label: "Sales Representative",
      options: salesReps.map((rep) => ({
        key: rep.id ? rep.id.toString() : "",
        value: rep.id
          ? `${rep.id} - ${rep.firstName} ${rep.lastName}`
          : `${rep.firstName} ${rep.lastName}`,
      })),
    },
  ];

  const handleRoleChange = (e, index, roleKey) => {
    const checked = e.target.checked;
    setAssignedContacts((prevContacts) =>
      prevContacts.map((contact, idx) =>
        idx === index ? { ...contact, [roleKey]: checked } : contact
      )
    );
  };

  const handleTabClick = (tabName) => {
    setSelectedTabs((prev) => {
      if (prev.includes(tabName)) {
        return prev.filter((t) => t !== tabName);
      } else {
        return [...prev, tabName];
      }
    });
  };

  const getButtonVariant = (tabName) => {
    return selectedTabs.includes(tabName) ? "contained" : "outlined";
  };

  return (
    <>
      <CloseConfirmationDialog
        open={showCloseConfirmation}
        onConfirm={() => {
          setShowCloseConfirmation(false);
          setIsEditMode(false);
          onClose();
        }}
        onCancel={() => setShowCloseConfirmation(false)}
      />

      {open && (
        <>
          <button
            onClick={() => {
              setPrev();
            }}
            disabled={!setPrev || isEditMode}
            style={{
              position: "fixed",
              left: `5%`,
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 1500,
            }}
            className={`p-2 rounded-full bg-white hover:bg-gray-200 transition ${
              !setPrev || isEditMode ? "opacity-50 hidden" : ""
            }`}
            aria-label="Previous Building"
          >
            <ArrowBackIcon />
          </button>

          <button
            onClick={() => {
              setNext();
            }}
            disabled={!setNext || isEditMode}
            style={{
              position: "fixed",
              right: `5%`,
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 1500,
            }}
            className={`p-2 rounded-full bg-white hover:bg-gray-200 transition ${
              !setNext || isEditMode ? "opacity-50 hidden" : ""
            }`}
            aria-label="Next Building"
          >
            <ArrowForwardIcon />
          </button>

          <Dialog
            open={open}
            onClose={handleClose}
            maxWidth={false}
            PaperProps={{
              style: {
                position: "absolute",
                top: navHeight,
                left: "50%",
                transform: "translateX(-50%)",
                margin: 4,
                transition: "width 0.3s ease, height 0.3s ease",
                width: isExpanded ? "90%" : "1000px",
                height: `calc(100vh - ${navHeight + 8}px)`,
                overflow: "auto",
              },
            }}
            fullWidth
            style={{ zIndex: 1400 }}
          >
            <div
              className={`shadow-lg transition-[flex-basis] duration-300 ease-in-out border-r border-gray-300 overflow-auto`}
              style={{
                flex: `0 0 ${panelWidth}`,
              }}
            >
              <TabPanel
                tabs={leftTabs}
                data={{
                  violations,
                  setViolations,
                  selectedViolation,
                  setSelectedViolation,
                  assignedInspections,
                  selectedInspection,
                  setSelectedInspection,
                }}
                isEditMode={isEditMode}
                handleFieldChange={editFieldHandler}
              />
            </div>
            <div
            //   style={{
            //     flex: "0 0 1000px",
            //     display: "flex",
            //     flexDirection: "column",
            //     overflow: "hidden",
            //   }}
            >
              <div className="sticky top-0 bg-white z-10">
                <div className="flex justify-between items-center p-4">
                  <div className="flex items-center space-x-4 flex-grow">
                    <button
                      onClick={handleClose}
                      className="p-2 rounded-full hover:bg-gray-200 transition"
                      aria-label="Back"
                    >
                      <ArrowBackIcon />
                    </button>
                    <h2 className="text-2xl font-bold text-left">
                      {isEditMode
                        ? `Edit Building - ${
                            buildingData.buildingName ||
                            `${buildingData.buildingReference}`
                          }`
                        : `View Building - ${
                            buildingData.buildingName ||
                            `${buildingData.buildingReference}`
                          }`}
                    </h2>
                  </div>
                  <button
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="p-2 rounded-full hover:bg-gray-200 transition"
                    aria-label={isExpanded ? "Shrink Modal" : "Expand Modal"}
                  >
                    {isExpanded ? <FullscreenExitIcon /> : <FullscreenIcon />}
                  </button>
                </div>
                <div className="p-4">
                  <TabPanelButtonGroup
                    selectedTabs={selectedTabs}
                    onTabClick={handleTabClick}
                  />
                </div>
                <div className="flex space-x-4">
                  <div className="flex space-x-4 p-4">
                    {isEditMode ? (
                      <>
                        <button
                          onClick={() => setIsEditMode(false)}
                          className="px-4 py-2 bg-sss-light-gray text-sss-blue-sel font-bold rounded-md hover:bg-gray-400 transition"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleSubmit}
                          className="px-6 py-2 bg-sss-accent-yel text-sss-blue-sel font-bold rounded-md hover:bg-yellow-600 transition"
                        >
                          Save
                        </button>
                        <button
                          onClick={handleDelete}
                          className="px-6 py-2 bg-red-500 text-white font-bold rounded-md hover:bg-red-600 transition"
                        >
                          Delete
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={toggleEditMode}
                        className="px-6 py-2 bg-sss-accent-yel text-sss-blue-sel font-bold rounded-md hover:bg-yellow-600 transition"
                      >
                        Edit
                      </button>
                    )}
                    <div className="justify-end mr-20">
                      <button
                        type="button"
                        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                        className="px-4 py-2 bg-blue-500 text-white font-bold rounded-md hover:bg-blue-600"
                      >
                        Actions
                      </button>
                      {isDropdownOpen && (
                        <div className="absolute mt-2 w-56 bg-white border border-gray-300 rounded-lg shadow-lg z-50">
                          <ul className="py-1">
                            <li
                              className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                              onClick={handleAddDevice}
                            >
                              Add a Device
                            </li>
                            <li
                              className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                              onClick={handleAccountCollections}
                            >
                              View Account Collections
                            </li>
                            <li
                              className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                              onClick={NavigateBuildingDeviceEditing}
                            >
                              Open Device Type Editing
                            </li>
                            <li
                              className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                              onClick={NavigateMaintenanceContractWizard}
                            >
                              Run the Add Maintenance Contract Wizard
                            </li>
                            <li
                              className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                              onClick={NavigateProposalWizard}
                            >
                              Run the Proposal Wizard
                            </li>
                            <li
                              className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                              onClick={() => {
                                setIsInspectionsWizardOpen(true); // <-- open local wizard
                                setIsDropdownOpen(false); // optional: close the menu
                              }}
                            >
                              Run the Inspection Wizard
                            </li>

                            <li
                              className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                              onClick={NavigateDOBPostings}
                            >
                              View DOB Postings
                            </li>
                            <li
                              className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                              onClick={NavigateRecordViewer}
                            >
                              View Building Records
                            </li>
                            <li
                              className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                              onClick={NavigateBuildingLedger}
                            >
                              View Building Ledger
                            </li>
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <DialogContent
                dividers
                style={{
                  minHeight: "500px",
                  width: "90%",
                  margin: "0 auto",
                  padding: 0,
                  height: "100%",
                }}
                sx={{ p: 0, pr: 2, pt: 2 }}
              >
                {isLoading ? (
                  <div className="flex justify-center items-center pt-80">
                    <ColorRing
                      visible={true}
                      height="100"
                      width="100"
                      ariaLabel="color-ring-loading"
                      wrapperStyle={{}}
                      wrapperClass="color-ring-wrapper"
                      colors={[
                        "#033B5C",
                        "#033B5C",
                        "#033B5C",
                        "#033B5C",
                        "#033B5C",
                        "#033B5C",
                      ]}
                    />
                  </div>
                ) : (
                  <div className="space-y-4">
                    <ContactAccordion label="Building Details">
                      <RenderFieldsVirtualizedAutocomplete
                        fields={buildingFieldsWithOptions}
                        formData={buildingData}
                        handleChange={handleFieldChange}
                        readOnly={!isEditMode}
                        style={{ width: "100%" }}
                        errors={errors}
                        renderError={renderError}
                      />
                      <RenderFieldsVirtualizedAutocomplete
                        fields={[
                          {
                            key: "buildingType",
                            label: "Building Type",
                            options: BuildingTypes.map((type) => ({
                              key: type.key,
                              value: type.label,
                            })),
                          },
                          {
                            key: "customerID",
                            label: "Customer",
                            options: customers.map((c) => ({
                              key: c.customerID.toString(),
                              value: c.customerName || "No Name",
                            })),
                          },
                        ]}
                        formData={buildingData}
                        handleChange={handleFieldChange}
                        readOnly={!isEditMode}
                      />
                    </ContactAccordion>

                    <ContactAccordion label="Address">
                      <div className="rounded-lg p-4 bg-white">
                        <div className="flex space-x-4 mb-4">
                          <button
                            type="button"
                            onClick={() => setActiveAddressSection("address")}
                            className={`px-4 py-2 rounded-md font-semibold ${
                              activeAddressSection === "address"
                                ? "bg-blue-500 text-white"
                                : "bg-gray-200 text-gray-700"
                            }`}
                          >
                            Address
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              setActiveAddressSection("billingAddress")
                            }
                            className={`px-4 py-2 rounded-md font-semibold ${
                              activeAddressSection === "billingAddress"
                                ? "bg-blue-500 text-white"
                                : "bg-gray-200 text-gray-700"
                            }`}
                          >
                            Billing Address
                          </button>
                        </div>
                        {activeAddressSection === "address" && (
                          <RenderFields
                            fields={AddressFields}
                            formData={buildingData}
                            handleChange={handleFieldChange}
                            readOnly={!isEditMode}
                            errors={errors}
                            renderError={renderError}
                          />
                        )}
                        {activeAddressSection === "billingAddress" && (
                          <RenderFields
                            fields={BillingAddressFields}
                            formData={buildingData}
                            handleChange={handleFieldChange}
                            readOnly={!isEditMode}
                            errors={errors}
                            renderError={renderError}
                          />
                        )}
                      </div>
                    </ContactAccordion>

                    <ContactAccordion label="Building Status">
                      <RenderFieldsDropdown
                        fields={[
                          {
                            key: "dispatchType",
                            label: "Dispatch Type",
                            options: DispatchTypes.map((type) => ({
                              key:
                                type.key !== undefined && type.key !== null
                                  ? type.key.toString()
                                  : "",
                              value: type.label || "",
                            })),
                          },
                          {
                            key: "buildingType",
                            label: "Building Type",
                            options: BuildingTypes.map((type) => ({
                              key:
                                type.key !== undefined && type.key !== null
                                  ? type.key.toString()
                                  : "",
                              value: type.label || "",
                            })),
                          },
                          {
                            key: "buildingStatus",
                            label: "Building Status",
                            options: StatusOptions.map((status) => ({
                              key:
                                status.key !== undefined && status.key !== null
                                  ? status.key.toString()
                                  : "",
                              value: status.label || "",
                            })),
                          },
                        ]}
                        formData={buildingData}
                        handleChange={handleFieldChange}
                        readOnly={!isEditMode}
                        errors={errors}
                        renderError={renderError}
                      />
                      {(() => {
                        const remainingFields = BuildingStatusFields.filter(
                          (field) =>
                            ![
                              "buildingStatus",
                              "billingClass",
                              "dispatchType",
                              "routeID",
                            ].includes(field.key)
                        );
                        return (
                          <div className="flex gap-4">
                            <div className="w-full">
                              <RenderFields
                                fields={remainingFields}
                                formData={buildingData}
                                handleChange={handleFieldChange}
                                readOnly={!isEditMode}
                              />
                            </div>
                          </div>
                        );
                      })()}
                    </ContactAccordion>

                    <ContactAccordion label="Building Sales">
                      <RenderFieldsVirtualizedAutocomplete
                        fields={salesFieldsWithOptions}
                        formData={buildingData}
                        handleChange={handleFieldChange}
                        readOnly={!isEditMode}
                      />
                      <RenderFields
                        fields={SalesMaintenanceFields}
                        formData={buildingData}
                        handleChange={handleFieldChange}
                        readOnly={!isEditMode}
                      />
                    </ContactAccordion>

                    <ContactAccordion label="Building Contacts">
                      {showNewContactForm && isEditMode && (
                        <NewBuildingContactForm
                          contactForm={contactForm}
                          handleChange={handleContactFormChange}
                          onCancel={handleCancelNewContact}
                          onSave={handleSaveNewContact}
                        />
                      )}
                      <BuildingContactTable
                        assignedContacts={assignedContacts}
                        setAssignedContacts={setAssignedContacts}
                        contactOptions={allContacts}
                        newContactId={newContactId}
                        setNewContactId={setNewContactId}
                        addContact={addContact}
                        removeContact={removeContact}
                        handleRoleChange={handleRoleChange}
                        isEditMode={isEditMode}
                        toggleNewContactForm={toggleNewContactForm}
                        updateContact={updateContact}
                      />
                    </ContactAccordion>

                    <ContactAccordion label="Building Devices">
                      {showNewDeviceForm && isEditMode && (
                        <NewBuildingDeviceForm
                          deviceForm={deviceForm}
                          handleDeviceFormChange={handleDeviceFormChange}
                          onCancel={handleCancelNewDevice}
                          onSave={handleSaveNewDevice}
                        />
                      )}
                      <BuildingDeviceTable
                        assignedDevices={assignedDevices}
                        setAssignedDevices={setAssignedDevices}
                        deviceOptions={allDevices}
                        newDeviceId={newDeviceId}
                        setNewDeviceId={setNewDeviceId}
                        addDevice={addDevice}
                        onUpdateDevice={updateDevice}
                        onRemoveDevice={removeDevice}
                        isEditMode={isEditMode}
                        toggleNewDeviceForm={toggleNewDeviceForm}
                      />
                    </ContactAccordion>
                  </div>
                )}
              </DialogContent>
            </div>
            <div
              className={`p-2 shadow-lg transition-[flex-basis] duration-300 ease-in-out border-l border-gray-300 overflow-auto`}
              style={{
                flex: `0 0 ${rightPanelWidth}`,
              }}
            >
              <TabPanel
                tabs={rightTabs}
                data={{
                  violations,
                  setViolations,
                  selectedViolation,
                  setSelectedViolation,
                  assignedInspections,
                  selectedInspection,
                  setSelectedInspection,
                }}
                isEditMode={isEditMode}
                handleFieldChange={editFieldHandler}
              />
            </div>
          </Dialog>

          {/* STEP 4: Conditionally render the "no dropdown" wizard here */}
          {isInspectionsWizardOpen && (
        <InspectionWizardController // <-- Use correct name
          isOpen={isInspectionsWizardOpen} // <-- PASS THE PROP HERE
          buildingId={buildingData?.buildingID} // Use optional chaining just in case
          onClose={() => setIsInspectionsWizardOpen(false)}
          refreshList={() => {
            // Add any specific refresh logic needed after wizard closes
             setIsInspectionsWizardOpen(false); // Close after refresh logic too
          }}
        />
      )}
        </>
      )}
    </>
  );
};

const CloseConfirmationDialog = ({ open, onConfirm, onCancel }) => (
  <Dialog
    open={open}
    onClose={onCancel}
    aria-labelledby="confirmation-dialog-title"
    aria-describedby="confirmation-dialog-description"
    style={{ zIndex: 1600 }}
  >
    <DialogContent>
      <div className="flex flex-col items-center">
        <p className="text-lg font-semibold" id="confirmation-dialog-title">
          Unsaved Changes
        </p>
        <p
          className="text-sm text-gray-600 mb-4"
          id="confirmation-dialog-description"
        >
          Please save or cancel your changes before closing.
        </p>
        <div className="flex space-x-4">
          <button
            onClick={onCancel}
            className="px-4 py-2 bg-gray-300 font-bold rounded-md hover:bg-gray-400 transition"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-blue-500 text-white font-bold rounded-md hover:bg-blue-600 transition"
          >
            Close Without Saving
          </button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);

export default ViewBuildingModal;
